import { useCallback, useEffect, useRef, type ReactNode } from 'react';
import { storeVideoFile, purgeOldEntries } from '@/utils/videoIndexedDb';
import { VideoSessionContext } from './VideoSessionContext';

// Split out of VideoSessionContext.tsx (react-refresh/only-export-components)
// — same rationale as AuthProvider.tsx: `useVideoSession` is imported
// widely, this Provider has few call sites. Behavior unchanged.
export function VideoSessionProvider({ children }: { children: ReactNode }) {
  // Map of videoId → Blob URL, kept in a ref to avoid re-renders on every registration
  const blobUrlsRef = useRef<Map<string, string>>(new Map());

  // On mount, purge IndexedDB entries older than 24 hours
  useEffect(() => {
    purgeOldEntries();

    // `blobUrlsRef.current` is the same Map instance for the provider's whole
    // lifetime (only ever mutated via .set/.clear, never reassigned), so
    // capturing it here and reading it at unmount below still sees every
    // registration made during the session — not a mount-time snapshot.
    const blobUrls = blobUrlsRef.current;

    // On unmount, revoke all Blob URLs to free memory
    return () => {
      blobUrls.forEach((url) => {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // Ignore revocation errors
        }
      });
      blobUrls.clear();
    };
  }, []);

  const registerUploadedFile = useCallback((videoId: string, file: File) => {
    // Create Blob URL immediately for in-session playback
    const blobUrl = URL.createObjectURL(file);
    blobUrlsRef.current.set(videoId, blobUrl);

    // Asynchronously persist to IndexedDB — do not await, failure is non-fatal
    storeVideoFile(videoId, file).catch(() => {
      // IndexedDB write failed (e.g. storage quota); in-memory Blob URL still works
    });
  }, []);

  const getBlobUrl = useCallback((videoId: string): string | null => {
    return blobUrlsRef.current.get(videoId) ?? null;
  }, []);

  return (
    <VideoSessionContext.Provider value={{ registerUploadedFile, getBlobUrl }}>
      {children}
    </VideoSessionContext.Provider>
  );
}
